import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, AlertTriangle, Info, Check, ChevronDown, ChevronRight,
  Mail, Building2, FileText, Lock, Loader2, CalendarRange, Wallet,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import UsageMeter from '../../components/billing/UsageMeter';
import InvoiceList from '../../components/billing/InvoiceList';
import { authService, clientService } from '../../services';
import { notifications } from '../../utils/toast';

// Shared chrome — matches admin Analytics / client SessionsPage.
const CARD_BASE =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)]';

const SECTION_HEADER_CHIP =
  'w-9 h-9 rounded-xl flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 text-neutral-600 dark:text-neutral-400 flex-shrink-0';

const PLAN_HIERARCHY = { free: 0, pro: 1 };

const PLAN_NAMES = {
  free: 'Free',
  pro: 'Pro',
};

// Feature matrix used to populate the Current Plan stats grid. Keeps the
// trainer page rendering a 2×2 grid (Sessions / Participants / Trainers /
// Storage) even when the backend doesn't denormalize features onto the
// subscription record.
const PLAN_FEATURES = {
  free: { sessions: 3, participants: 30, trainers: 1, storage: 100 },
  pro: { sessions: 20, participants: 30, trainers: 1, storage: 1024 },
};

/**
 * Trainer Billing Page — solo trainers (individual org type).
 *
 * Mirrors client SubscriptionPage's full UX (pill main tabs, sub-tabs,
 * Current Plan card, Usage Statistics, Billing Details, Subscription
 * Actions, modals) but constrained to Free + Pro plans.
 */
const TrainerBillingPage = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [scheduledDowngrade, setScheduledDowngrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Two-level tab structure — same pattern as client SubscriptionPage.
  const [mainTab, setMainTab] = useState('subscription');
  const [subTab, setSubTab] = useState('overview');

  // Modal states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedPlanForAction, setSelectedPlanForAction] = useState(null);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Guard: only solo trainers (individual org) can access billing
  useEffect(() => {
    const user = authService.getCurrentUser();
    const org = user?.primaryOrganization || user?.organization;
    if (org?.type !== 'individual') {
      navigate('/trainer/dashboard', { replace: true });
    }
  }, [navigate]);

  // Solo trainer plans — Free and Pro only.
  const plans = [
    {
      id: 'free',
      name: 'Free',
      monthlyPrice: 0,
      description: 'Try Aktivity free for 1 month. Upgrade to continue beyond that.',
      features: [
        '3 sessions total (one-time)',
        'Up to 30 participants/session',
        'All core features',
        '1 month only — no renewal',
      ],
      buttonLabel: 'Start Free',
    },
    {
      id: 'pro',
      name: 'Pro',
      monthlyPrice: 1999,
      description: 'For trainers who want proof of impact',
      features: [
        '20 sessions/month',
        '30 participants/session',
        '1 trainer access',
        'Pre & post session features',
        'AI-powered insights',
      ],
      buttonLabel: 'Choose Pro',
    },
  ];

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);

      const [subResponse, , invoiceResponse] = await Promise.all([
        clientService.getSubscription(),
        clientService.getBillingOverview(),
        clientService.getInvoices({ limit: 50 }),
      ]);

      if (subResponse.success) {
        const sub = subResponse.data;
        const planId = sub.plan || sub.subscription?.plan || 'free';
        const subData = sub.subscription || {};
        const apiUsage = sub.usage || {};

        // getSubscription returns these at the top level of `data`, not nested
        // under `subscription` — read from either so the period dates and
        // downgrade banner populate.
        const scheduledDowngrade = subData.scheduledDowngrade || sub.scheduledDowngrade;
        if (scheduledDowngrade?.plan) {
          setScheduledDowngrade(scheduledDowngrade);
        } else {
          setScheduledDowngrade(null);
        }

        const rawStart = subData.currentPeriodStart || sub.currentPeriodStart;
        const rawEnd = subData.currentPeriodEnd || sub.currentPeriodEnd;
        const periodStart = rawStart ? new Date(rawStart).toISOString().split('T')[0] : '';
        const periodEnd = rawEnd ? new Date(rawEnd).toISOString().split('T')[0] : '';

        // Prefer the API's features (they reflect any admin participant-cap
        // override); fall back to the static map only if absent.
        const features = sub.features || PLAN_FEATURES[planId] || PLAN_FEATURES.free;
        const price = subData.price || 0;
        const gstRate = subData.gstRate || (price > 0 ? 18 : 0);
        const gstAmount = subData.gstAmount || (price > 0 ? Math.round(price * 0.18) : 0);
        const totalWithGst = subData.totalWithGst || (price > 0 ? price + gstAmount : 0);

        setSubscription({
          id: subData._id,
          plan: PLAN_NAMES[planId] || 'Free',
          planId,
          price,
          gstRate,
          gstAmount,
          totalWithGst,
          interval: subData.interval || subData.billingPeriod || 'month',
          status: sub.subscriptionStatus || subData.status || 'active',
          currentPeriodStart: periodStart,
          currentPeriodEnd: periodEnd,
          cancelAtPeriodEnd: subData.cancelAtPeriodEnd || false,
          autoRenew: !subData.cancelAtPeriodEnd,
          expired: sub.expired || false,
          features,
          maxParticipantsOverride: sub.maxParticipantsOverride || null,
        });

        // Usage shape — match what client SubscriptionPage stores so the
        // shared UsageMeter component renders identically.
        setUsage({
          sessions: {
            used: apiUsage.sessions?.used ?? 0,
            limit: apiUsage.sessions?.limit ?? features.sessions,
          },
          participants: {
            used: apiUsage.participants?.used ?? 0,
            limit: apiUsage.participants?.limit ?? features.participants,
          },
          trainers: {
            used: apiUsage.trainers?.used ?? 1,
            limit: apiUsage.trainers?.limit ?? features.trainers,
          },
          storage: {
            used: apiUsage.storage?.used ?? 0,
            limit: apiUsage.storage?.limit ?? features.storage,
          },
        });
      }

      if (invoiceResponse?.success) {
        setInvoices(invoiceResponse.data?.invoices || invoiceResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanPrice = (plan) => {
    if (plan.monthlyPrice === 0) return { price: 0, interval: 'forever' };
    return { price: plan.monthlyPrice, interval: 'month' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const currentPlanId = subscription?.planId || 'free';

  const handleSelectPlan = (planId) => {
    if (planId === currentPlanId) return;

    const currentLevel = PLAN_HIERARCHY[currentPlanId] ?? 0;
    const selectedLevel = PLAN_HIERARCHY[planId] ?? 0;

    setSelectedPlanForAction(planId);

    if (selectedLevel > currentLevel) {
      setShowUpgradeModal(true);
    } else if (selectedLevel < currentLevel) {
      setShowDowngradeModal(true);
    }
  };

  const handleConfirmUpgrade = async () => {
    const planId = selectedPlanForAction;
    setShowUpgradeModal(false);

    try {
      setCheckoutLoading(true);
      localStorage.setItem('pending_plan_id', planId);
      localStorage.setItem('pending_billing_cycle', 'monthly');

      const response = await clientService.createCheckoutSession(planId, 'monthly');

      if (response.success && response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        notifications.error('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      notifications.error(error.response?.data?.error || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleConfirmDowngrade = async () => {
    const planId = selectedPlanForAction;

    try {
      setDowngradeLoading(true);
      const response = await clientService.scheduleDowngrade(planId);

      if (response.success) {
        setShowDowngradeModal(false);
        notifications.success(`Your plan will be downgraded to ${PLAN_NAMES[planId]} at the end of your billing period.`);
        loadBillingData();
      }
    } catch (error) {
      console.error('Downgrade error:', error);
      notifications.error(error.response?.data?.error || 'Failed to schedule downgrade. Please try again.');
    } finally {
      setDowngradeLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    try {
      setCancelLoading(true);
      const response = await clientService.cancelSubscription(cancelReason);
      if (response.success) {
        setShowCancelModal(false);
        setCancelReason('');
        notifications.success('Your subscription will be cancelled at the end of the billing period.');
        loadBillingData();
      }
    } catch (error) {
      console.error('Cancel error:', error);
      notifications.error(error.response?.data?.error || 'Failed to cancel subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      const response = await clientService.reactivateSubscription();
      if (response.success) {
        notifications.success('Subscription reactivated. Auto-renewal is back on.');
        loadBillingData();
      }
    } catch (error) {
      console.error('Reactivate error:', error);
      notifications.error(error.response?.data?.error || 'Failed to reactivate subscription.');
    }
  };

  const handleCancelScheduledDowngrade = async () => {
    try {
      const response = await clientService.cancelScheduledDowngrade();
      if (response.success) {
        notifications.success('Scheduled downgrade cancelled.');
        loadBillingData();
      }
    } catch (error) {
      console.error('Cancel downgrade error:', error);
      notifications.error(error.response?.data?.error || 'Failed to cancel scheduled downgrade.');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto">
          <div className="animate-pulse space-y-5">
            <div className="h-8 bg-neutral-200 dark:bg-neutral-800 rounded w-48" />
            <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-72" />
            <div className="h-10 bg-neutral-200 dark:bg-neutral-800 rounded w-80" />
            <div className="h-64 bg-neutral-200 dark:bg-neutral-800 rounded-2xl" />
            <div className="h-48 bg-neutral-200 dark:bg-neutral-800 rounded-2xl" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto space-y-5 pb-20 sm:pb-6">
        {/* Header */}
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => navigate('/trainer/dashboard')}
            className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 transition-colors mb-1 group"
          >
            <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">Subscription &amp; Billing</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Manage your subscription, billing, and view available plans</p>
        </div>

        {/* Main Tabs — pill chip toggle. */}
        <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 flex-wrap w-full sm:w-auto">
          {[
            { value: 'subscription', label: 'Subscription Details & Billing' },
            { value: 'plans',        label: 'All Plans' },
          ].map((o) => {
            const active = mainTab === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => setMainTab(o.value)}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30 shadow-sm'
                    : 'text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100'
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>

        {/* ===== Subscription Details & Billing Tab ===== */}
        {mainTab === 'subscription' && (
          <div className="space-y-5">
            {/* Sub-Tabs — underline indicator */}
            <div className="border-b border-neutral-200 dark:border-neutral-800/70">
              <div className="flex gap-4 sm:gap-6 overflow-x-auto">
                {[
                  { id: 'overview', label: 'Overview' },
                  { id: 'payment-methods', label: 'Payment Methods' },
                  { id: 'invoices', label: 'Invoices' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSubTab(tab.id)}
                    className={`pb-3 text-sm font-medium transition-colors relative whitespace-nowrap ${
                      subTab === tab.id
                        ? 'text-primary-600'
                        : 'text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'
                    }`}
                  >
                    {tab.label}
                    {subTab === tab.id && (
                      <motion.div
                        layoutId="trainerSubTabIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-t"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* ----- Overview ----- */}
            {subTab === 'overview' && (
              <div className="space-y-4">
                {/* Cancellation banner */}
                {subscription?.cancelAtPeriodEnd && (
                  <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 ring-1 ring-inset ring-amber-200/60 dark:ring-amber-500/20 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={20} strokeWidth={1.75} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Subscription Cancelled</p>
                        <p className="text-sm text-amber-800/90 dark:text-amber-300/90 mt-1">
                          Your subscription will be cancelled on {formatDate(subscription.currentPeriodEnd)}.
                        </p>
                        <Button variant="primary" size="sm" onClick={handleReactivateSubscription} className="mt-3">
                          Reactivate Subscription
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Scheduled downgrade banner */}
                {scheduledDowngrade?.plan && (
                  <div className="rounded-xl bg-blue-50 dark:bg-blue-500/10 ring-1 ring-inset ring-blue-200/60 dark:ring-blue-500/20 p-4">
                    <div className="flex items-start gap-3">
                      <Info size={20} strokeWidth={1.75} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">Downgrade Scheduled</p>
                        <p className="text-sm text-blue-800/90 dark:text-blue-300/90 mt-1">
                          Your plan will change to <strong>{PLAN_NAMES[scheduledDowngrade.plan]}</strong> on{' '}
                          {formatDate(scheduledDowngrade.scheduledDate)}. You'll keep your current plan until then.
                        </p>
                        <Button variant="ghost" size="sm" onClick={handleCancelScheduledDowngrade} className="mt-3">
                          Cancel Downgrade
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Current Plan card — flips to "Previous Plan (Expired)" when expired. */}
                {subscription?.expired ? (
                  <div className={`${CARD_BASE} p-6`}>
                    <div className="flex items-start gap-3 mb-4">
                      <span className={SECTION_HEADER_CHIP}>
                        <Wallet size={16} strokeWidth={1.75} />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Previous Plan (Expired)</h2>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Upgrade to restore session creation</p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50 capitalize">{subscription.planId} Plan</h3>
                          <Badge variant="error">Expired</Badge>
                        </div>
                        <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-3">
                          Your previous plan has expired.{' '}
                          {subscription.currentPeriodEnd && (
                            <>
                              Your <span className="capitalize">{subscription.planId}</span> plan ended on{' '}
                              {formatDate(subscription.currentPeriodEnd)}.{' '}
                            </>
                          )}
                          Upgrade now to restore session creation and live activities.
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          Existing data is preserved — your sessions and reports remain intact.
                        </p>
                      </div>
                      <Button variant="primary" size="sm" onClick={() => setMainTab('plans')} className="self-start sm:self-auto flex-shrink-0">
                        Upgrade Plan
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className={`${CARD_BASE} p-6`}>
                    <div className="flex items-start gap-3 mb-4">
                      <span className={SECTION_HEADER_CHIP}>
                        <Wallet size={16} strokeWidth={1.75} />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Current Plan</h2>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Your active subscription</p>
                      </div>
                    </div>
                    {subscription ? (
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50">{subscription.plan} Plan</h3>
                            <Badge variant={subscription.status === 'active' ? 'success' : 'warning'}>
                              {subscription.status}
                            </Badge>
                          </div>
                          {subscription.price > 0 ? (
                            <div className="mb-4">
                              <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 tabular-nums">
                                ₹{subscription.totalWithGst?.toLocaleString('en-IN')}
                                <span className="text-sm font-normal text-neutral-500 dark:text-neutral-400">/{subscription.interval}</span>
                              </p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                ₹{subscription.price?.toLocaleString('en-IN')} + ₹{subscription.gstAmount?.toLocaleString('en-IN')} GST ({subscription.gstRate}%)
                              </p>
                            </div>
                            ) : subscription.planId === "free" ? (
                              <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-4">Free</p>
                            ) : (
                              /* A paid tier at zero cost — an admin comp, not the Free plan.
                                 Saying "Free" here contradicts the "Pro Plan" heading above it. */
                              <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-4">
                                ₹0 <span className="text-sm">— granted by admin</span>
                              </p>
                            )}

                          {/* 2×2 stats grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 sm:gap-y-3 text-sm">
                            <div className="flex items-center gap-2 sm:block">
                              <span className="text-neutral-500 dark:text-neutral-400 min-w-[90px] sm:min-w-0">Sessions:</span>
                              <span className="sm:ml-2 font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
                                {subscription.features.sessions === -1 ? 'Unlimited' : `${subscription.features.sessions}/month`}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 sm:block">
                              <span className="text-neutral-500 dark:text-neutral-400 min-w-[90px] sm:min-w-0">Participants:</span>
                              <span className="sm:ml-2 font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
                                {subscription.features.participants === -1 ? 'Unlimited' : `${subscription.features.participants}/session`}
                              </span>
                              {subscription.maxParticipantsOverride && (
                                <span className="sm:ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30">
                                  Increased by admin
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 sm:block">
                              <span className="text-neutral-500 dark:text-neutral-400 min-w-[90px] sm:min-w-0">Trainers:</span>
                              <span className="sm:ml-2 font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
                                {subscription.features.trainers === -1 ? 'Unlimited' : subscription.features.trainers}
                              </span>
                            </div>
                          </div>
                        </div>

                        <Button variant="primary" size="sm" onClick={() => setMainTab('plans')} className="self-start sm:self-auto flex-shrink-0">
                          Change Plan
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-2">Free Plan</h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">Upgrade to unlock more features!</p>
                        <Button variant="primary" onClick={() => setMainTab('plans')}>
                          View Plans &amp; Upgrade
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Usage Statistics */}
                {usage && (
                  <div className={`${CARD_BASE} p-6`}>
                    <div className="flex items-start gap-3 mb-4">
                      <span className={SECTION_HEADER_CHIP}>
                        <CalendarRange size={16} strokeWidth={1.75} />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Usage Statistics</h2>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Current period usage</p>
                      </div>
                    </div>
                    <div className="space-y-6">
                      <UsageMeter
                        label="Total Sessions Created"
                        used={usage.sessions.used}
                        limit={usage.sessions.limit}
                        showUpgrade
                        onUpgrade={() => setMainTab('plans')}
                      />
                      <UsageMeter
                        label="Trainers"
                        used={usage.trainers.used}
                        limit={usage.trainers.limit}
                        showUpgrade
                        onUpgrade={() => setMainTab('plans')}
                      />
                    </div>
                  </div>
                )}

                {/* Billing Details */}
                {subscription && (
                  <div className={`${CARD_BASE} p-6`}>
                    <div className="flex items-start gap-3 mb-4">
                      <span className={SECTION_HEADER_CHIP}>
                        <FileText size={16} strokeWidth={1.75} />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Billing Details</h2>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Current period &amp; renewal</p>
                      </div>
                    </div>
                    <div className="space-y-3 text-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5">
                        <span className="text-neutral-500 dark:text-neutral-400">Current Period</span>
                        <span className="font-medium tabular-nums text-neutral-900 dark:text-neutral-100">
                          {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-500 dark:text-neutral-400">Next Billing Date</span>
                        <span className="font-medium tabular-nums text-neutral-900 dark:text-neutral-100">
                          {formatDate(subscription.currentPeriodEnd)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-500 dark:text-neutral-400">Next Payment Amount</span>
                        <span className="font-medium tabular-nums text-neutral-900 dark:text-neutral-100">
                          {subscription.cancelAtPeriodEnd
                            ? '₹0.00 (Cancelled)'
                            : `₹${subscription.totalWithGst?.toLocaleString('en-IN')}`}
                        </span>
                      </div>
                      {!subscription.cancelAtPeriodEnd && subscription.price > 0 && (
                        <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
                          <span>Breakdown</span>
                          <span className="tabular-nums">
                            ₹{subscription.price?.toLocaleString('en-IN')} + ₹{subscription.gstAmount?.toLocaleString('en-IN')} GST ({subscription.gstRate}%)
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-500 dark:text-neutral-400">Auto-Renew</span>
                        <Badge variant={subscription.autoRenew ? 'success' : 'warning'}>
                          {subscription.autoRenew ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                )}

                {/* Subscription Actions — only for active paid subscriptions. */}
                {subscription && !subscription.cancelAtPeriodEnd && subscription.price > 0 && (
                  <div className={`${CARD_BASE} p-6`}>
                    <div className="flex items-start gap-3 mb-4">
                      <span className={SECTION_HEADER_CHIP}>
                        <AlertTriangle size={16} strokeWidth={1.75} />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Subscription Actions</h2>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Manage cancellation</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="rounded-xl bg-neutral-50/70 dark:bg-neutral-900/60 ring-1 ring-inset ring-neutral-100 dark:ring-neutral-800 p-4">
                        <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
                          Your subscription renews automatically on <strong>{formatDate(subscription.currentPeriodEnd)}</strong>. The payment will be charged to your saved payment method. If you don't want to be charged for the next billing cycle, you can cancel your subscription below. You'll continue to have full access until the end of your current billing period.
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-semibold text-rose-700 dark:text-rose-400 mb-1">Cancel Subscription</h4>
                          <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">
                            Stops auto-renewal. You'll have access until {formatDate(subscription.currentPeriodEnd)}.
                          </p>
                        </div>
                        <Button variant="danger" size="sm" onClick={() => setShowCancelModal(true)} className="self-start sm:self-auto flex-shrink-0">
                          Cancel Plan
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ----- Payment Methods ----- */}
            {subTab === 'payment-methods' && (
              <div className={`${CARD_BASE} p-6`}>
                <div className="flex items-start gap-3 mb-4">
                  <span className={SECTION_HEADER_CHIP}>
                    <Lock size={16} strokeWidth={1.75} />
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Payment Methods</h2>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Managed via secure checkout</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="rounded-xl bg-blue-50 dark:bg-blue-500/10 ring-1 ring-inset ring-blue-200/60 dark:ring-blue-500/20 p-4">
                    <div className="flex items-start gap-3">
                      <Lock size={20} strokeWidth={1.75} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">Secure Payment Processing</p>
                        <p className="text-sm text-blue-800/90 dark:text-blue-300/90">
                          Payment methods are securely managed through our payment partner, Dodo Payments. When you subscribe or upgrade your plan, you'll be redirected to a secure checkout page to enter your payment details.
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Your payment information is never stored on our servers. To add or update a payment method, choose a plan and complete the secure checkout process.
                  </p>
                  <Button variant="primary" onClick={() => setMainTab('plans')}>
                    View Plans
                  </Button>
                </div>
              </div>
            )}

            {/* ----- Invoices ----- */}
            {subTab === 'invoices' && (
              <div className={`${CARD_BASE} p-6`}>
                <div className="flex items-start gap-3 mb-4">
                  <span className={SECTION_HEADER_CHIP}>
                    <FileText size={16} strokeWidth={1.75} />
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 leading-tight">Billing History</h2>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">All past invoices</p>
                  </div>
                </div>
                {invoices.length === 0 ? (
                  <div className="py-10 text-center">
                    <span className="inline-flex w-14 h-14 rounded-2xl items-center justify-center bg-neutral-100 text-neutral-400 ring-1 ring-inset ring-neutral-200 dark:bg-neutral-800/70 dark:text-neutral-500 dark:ring-neutral-700/60 mb-4">
                      <FileText size={22} strokeWidth={1.75} />
                    </span>
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-1">No invoices yet</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      Your billing history will appear here once you upgrade to a paid plan.
                    </p>
                  </div>
                ) : (
                  <InvoiceList invoices={invoices} />
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== All Plans Tab ===== */}
        {mainTab === 'plans' && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50 mb-2">Choose the Right Plan for You</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-2">
                Select a plan that fits your training needs. Upgrade or downgrade anytime.
              </p>
              <p className="text-xs text-neutral-400 dark:text-neutral-500">Solo trainer plans are billed monthly.</p>
            </div>

            {/* Free is one-time only: hide its card for any solo trainer not currently on Free. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {plans
                .filter((plan) => plan.id !== 'free' || currentPlanId === 'free')
                .map((plan, index) => {
                  const priceInfo = getPlanPrice(plan);
                  const isCurrentPlan = currentPlanId === plan.id;

                  return (
                    <motion.div
                      key={plan.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + index * 0.1 }}
                      className={`relative rounded-2xl bg-white dark:bg-neutral-900 p-6 flex flex-col transition-all ${
                        isCurrentPlan
                          ? 'ring-2 ring-primary-400 dark:ring-primary-500/50 shadow-[0_4px_16px_-4px_rgba(255,159,67,0.25)]'
                          : 'ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)] hover:ring-neutral-300 dark:hover:ring-neutral-700'
                      }`}
                    >
                      {isCurrentPlan && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span className="bg-gradient-to-r from-amber-500 to-primary-500 text-white text-[11px] font-semibold uppercase tracking-[0.06em] px-3 py-1 rounded-full shadow-sm">
                            Current Plan
                          </span>
                        </div>
                      )}

                      <div className="flex-1">
                        <div className="text-center mb-6">
                          <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50 mb-2">{plan.name}</h3>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4 leading-relaxed">{plan.description}</p>

                          <div className="mb-2">
                            <div className="flex items-baseline justify-center gap-1 flex-wrap">
                              <span className="text-3xl font-semibold tabular-nums tracking-tight text-neutral-900 dark:text-neutral-50">
                                ₹{priceInfo.price.toLocaleString('en-IN')}
                              </span>
                              <span className="text-sm text-neutral-500 dark:text-neutral-400">/{priceInfo.interval}</span>
                              {priceInfo.price > 0 && (
                                <span className="text-xs text-neutral-400">+GST</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <ul className="space-y-3 mb-6">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                              <span className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check size={12} strokeWidth={2.5} />
                              </span>
                              <span className="leading-relaxed">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <button
                        type="button"
                        className={`w-full py-2.5 px-4 rounded-xl font-semibold text-sm transition-all mt-auto ${
                          isCurrentPlan
                            ? 'bg-gradient-to-r from-amber-500 to-primary-500 text-white cursor-default'
                            : 'bg-neutral-900 hover:bg-neutral-800 text-white dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200'
                        }`}
                        onClick={() => handleSelectPlan(plan.id)}
                        disabled={isCurrentPlan || checkoutLoading}
                      >
                        {checkoutLoading
                          ? 'Redirecting…'
                          : isCurrentPlan
                            ? 'Current Plan'
                            : plan.buttonLabel}
                      </button>
                    </motion.div>
                  );
                })}
            </div>

            {/* FAQ Section */}
            <div>
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50 mb-2">Frequently Asked Questions</h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Everything you need to know about Aktivity pricing and subscriptions</p>
              </div>

              <div className="max-w-3xl mx-auto space-y-3">
                {[
                  {
                    question: 'Is Aktivity free to use?',
                    answer: 'Aktivity offers a one-time Free plan with 3 sessions and up to 30 participants per session, valid for 1 month. After that, you’ll need to upgrade to Pro to keep running sessions. This ensures Aktivity is used in real training environments, not demos.',
                  },
                  {
                    question: 'What happens after my Free month ends?',
                    answer: 'Once your Free month is over, you’ll be prompted to upgrade to Pro to continue running sessions and accessing reports. Your data remains safe and available when you upgrade.',
                  },
                  {
                    question: 'Can I switch back to Free after upgrading?',
                    answer: 'No. Free is a one-time starter plan available only at signup. Once you upgrade or your Free month ends, the only way to keep using Aktivity is on a paid plan.',
                  },
                  {
                    question: 'Do Free and Pro include the same features?',
                    answer: 'Yes — both plans include all core learning features (reflections, challenges, action plans, feedback, reports, AI insights, pre & post-session). The difference is volume: Free includes 3 sessions one-time; Pro includes 20 sessions/month.',
                  },
                  {
                    question: 'How do I upgrade to Pro?',
                    answer: 'Go to the All Plans tab, choose Pro, and you\'ll be redirected to a secure checkout. Once payment is complete, your Pro features are active immediately.',
                  },
                  {
                    question: 'Can I cancel my Pro subscription?',
                    answer: 'Yes. You can cancel anytime from the Subscription Actions card. You\'ll keep full access until the end of your current billing period, then your account will pause until you upgrade again — Free isn\'t available as a fallback.',
                  },
                  {
                    question: 'Is Aktivity an LMS?',
                    answer: 'No. Aktivity doesn\'t replace your LMS. It complements your LMS by capturing what LMS platforms don\'t: learner challenges, reflection notes, action plans, and topic-level learning intelligence.',
                  },
                  {
                    question: 'Can Aktivity be used for in-person training?',
                    answer: 'Absolutely. Aktivity is designed for instructor-led training (ILT), virtual sessions (VILT), and hybrid programs — even when cameras are off.',
                  },
                  {
                    question: 'How is Aktivity different from Slido or Mentimeter?',
                    answer: 'Engagement tools focus on interaction during sessions. Aktivity focuses on learning intelligence after the interaction. We don\'t just ask questions — we capture what learners struggled with, what they committed to apply, and how confident they feel to act.',
                  },
                  {
                    question: 'Need more than Pro?',
                    answer: 'Solo trainer accounts are capped at Pro. If you need unlimited sessions, more trainer accounts, priority support, or advanced analytics, you can move to an organization account with Pro Plus or Enterprise plans. Contact us at support@aktivity.io to get set up.',
                  },
                  {
                    question: 'Is my data secure?',
                    answer: 'Yes. All data is securely stored and accessible only to you. We follow standard security and access best practices, and payments are handled by Dodo Payments — your card details are never stored on our servers.',
                  },
                ].map((faq, index) => (
                  <details
                    key={index}
                    className={`group ${CARD_BASE} overflow-hidden`}
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-neutral-50/70 dark:hover:bg-neutral-900/60 transition-colors gap-4">
                      <span className="font-medium text-neutral-900 dark:text-neutral-100 text-sm sm:text-base">{faq.question}</span>
                      <ChevronDown size={18} strokeWidth={1.75} className="text-neutral-400 flex-shrink-0 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="px-5 pb-5 text-sm text-neutral-600 dark:text-neutral-400 border-t border-neutral-100 dark:border-neutral-800/70 pt-4 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>

              {/* Contact Support */}
              <div className="text-center mt-8 pt-8 border-t border-neutral-200 dark:border-neutral-800/70">
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-1">Still have questions?</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">Talk to us — we'll help you pick the right plan for your training.</p>
                <a
                  href="mailto:support@aktivity.io"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                >
                  <Mail size={16} strokeWidth={1.75} />
                  Contact Support
                </a>
              </div>
            </div>

            {/* Upsell to Organization */}
            <div className="rounded-2xl bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-500/10 dark:to-blue-500/10 ring-1 ring-inset ring-primary-200/60 dark:ring-primary-500/20 p-6">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100 dark:bg-primary-500/15 dark:text-primary-300 dark:ring-primary-500/30 flex-shrink-0">
                  <Building2 size={20} strokeWidth={1.75} />
                </span>
                <div>
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mb-1">Need more?</h3>
                  <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
                    Create an organization account to access Pro Plus and Enterprise plans. Get unlimited sessions, more trainer accounts, priority support, and advanced analytics.
                  </p>
                  <a
                    href="mailto:support@aktivity.io?subject=Organization Account Inquiry&body=Hi, I'm a solo trainer and I'd like to learn about upgrading to an organization account for access to Pro Plus and Enterprise plans."
                    className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mt-3 transition-colors"
                  >
                    Contact us to get started
                    <ChevronRight size={14} strokeWidth={2} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Confirmation Modal */}
      <Modal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} title="Confirm Upgrade" size="md">
        <div>
          <div className="mb-6">
            <p className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
              Upgrade to {PLAN_NAMES[selectedPlanForAction]}
            </p>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 leading-relaxed">
              You'll be redirected to a secure checkout to complete your upgrade. Your current subscription will be cancelled and you'll be charged for the new plan immediately.
            </p>

            <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 ring-1 ring-inset ring-amber-200/60 dark:ring-amber-500/20 p-4 mb-4">
              <p className="text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
                <strong>Note:</strong> Your remaining days on the current plan are not prorated. The new plan starts immediately after payment.
              </p>
            </div>

            <div className="rounded-xl bg-neutral-50 dark:bg-neutral-900 ring-1 ring-inset ring-neutral-100 dark:ring-neutral-800 p-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-neutral-500 dark:text-neutral-400">Current Plan</span>
                <span className="font-medium text-neutral-900 dark:text-neutral-100">{PLAN_NAMES[currentPlanId] || 'Free'}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500 dark:text-neutral-400">New Plan</span>
                <span className="font-medium text-primary-600 dark:text-primary-400">{PLAN_NAMES[selectedPlanForAction]}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setShowUpgradeModal(false)} fullWidth>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmUpgrade} disabled={checkoutLoading} fullWidth>
              {checkoutLoading ? (
                <>
                  <Loader2 size={14} strokeWidth={1.75} className="animate-spin mr-1.5" />
                  Redirecting…
                </>
              ) : 'Proceed to Checkout'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Downgrade Confirmation Modal */}
      <Modal isOpen={showDowngradeModal} onClose={() => setShowDowngradeModal(false)} title="Confirm Downgrade" size="md">
        <div>
          <div className="mb-6">
            <p className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
              Downgrade to {PLAN_NAMES[selectedPlanForAction]}
            </p>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 leading-relaxed">
              Your plan will change to {PLAN_NAMES[selectedPlanForAction]} at the end of your current billing period. You'll keep full access to your current plan until then.
            </p>

            <div className="rounded-xl bg-neutral-50 dark:bg-neutral-900 ring-1 ring-inset ring-neutral-100 dark:ring-neutral-800 p-4 mb-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-neutral-500 dark:text-neutral-400">Current Plan</span>
                <span className="font-medium text-neutral-900 dark:text-neutral-100">{PLAN_NAMES[currentPlanId] || 'Free'}</span>
              </div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-neutral-500 dark:text-neutral-400">New Plan (after period ends)</span>
                <span className="font-medium text-amber-600 dark:text-amber-400">{PLAN_NAMES[selectedPlanForAction]}</span>
              </div>
              {subscription?.currentPeriodEnd && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-500 dark:text-neutral-400">Change Takes Effect</span>
                  <span className="font-medium tabular-nums text-neutral-900 dark:text-neutral-100">{formatDate(subscription.currentPeriodEnd)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setShowDowngradeModal(false)} fullWidth>
              Keep Current Plan
            </Button>
            <Button variant="danger" onClick={handleConfirmDowngrade} disabled={downgradeLoading} fullWidth>
              {downgradeLoading ? (
                <>
                  <Loader2 size={14} strokeWidth={1.75} className="animate-spin mr-1.5" />
                  Scheduling…
                </>
              ) : 'Confirm Downgrade'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancel Subscription Modal */}
      <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} title="Cancel Subscription" size="md">
        <div>
          <div className="mb-6">
            <p className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
              Cancel your {PLAN_NAMES[currentPlanId]} subscription
            </p>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 leading-relaxed">
              You'll keep full access until {subscription?.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : 'the end of your billing period'}.
              After that your account will be locked until you upgrade again — Free isn't available as a fallback (one-time only).
            </p>

            <label className="block text-sm font-medium text-neutral-900 dark:text-neutral-100 mb-2">
              Tell us why (optional)
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Help us improve — what's not working?"
              className="w-full px-3 py-2 rounded-xl bg-neutral-50 dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 placeholder:text-neutral-400 text-neutral-900 dark:text-neutral-100"
            />
          </div>

          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setShowCancelModal(false)} fullWidth>
              Keep Subscription
            </Button>
            <Button variant="danger" onClick={handleConfirmCancel} disabled={cancelLoading} fullWidth>
              {cancelLoading ? (
                <>
                  <Loader2 size={14} strokeWidth={1.75} className="animate-spin mr-1.5" />
                  Cancelling…
                </>
              ) : 'Confirm Cancel'}
            </Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default TrainerBillingPage;
